// Copyright (c) 2025 GeoVerses. All rights reserved.

import React, { useRef, useCallback, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import InfoIcon from '@mui/icons-material/Info';
import BugReportIcon from '@mui/icons-material/BugReport';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { Icon } from 'leaflet';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Circle,
  useMap,
  useMapEvents
} from 'react-leaflet';
import "leaflet/dist/leaflet.css";
import plume from '../components/plume.png';

const icon = new Icon({
    iconUrl: plume,
    iconSize: [50, 50],
    iconAnchor: [1, 45]
});

const CITY_ZOOM = 11;

const RecenterControl = ({ latitude, longitude, recenterRef, locationReady, zoomLevel, isVisible }) => {
  const map = useMap();
  const hasInitiallyPanned = useRef(false);
  const latRef = useRef(latitude);
  const lngRef = useRef(longitude);
  const zoomRef = useRef(zoomLevel);
  const locationReadyRef = useRef(locationReady);

  // Keep refs current on every render
  latRef.current = latitude;
  lngRef.current = longitude;
  locationReadyRef.current = locationReady;

  useEffect(() => {
    if (locationReadyRef.current && !hasInitiallyPanned.current) {
      hasInitiallyPanned.current = true;
      map.flyTo([latRef.current, lngRef.current], zoomRef.current, { duration: 1.5 });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationReady]);

  useEffect(() => {
    if (isVisible && locationReadyRef.current) {
      map.setView([latRef.current, lngRef.current]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  recenterRef.current = () => map.setView([latRef.current, lngRef.current]);
  return null;
};

const ZoomTracker = ({ onZoomEnd }) => {
  useMapEvents({
    zoomend: (e) => {
      onZoomEnd({ zoom: e.target.getZoom() });
    }
  });
  return null;
};

const PoemMap = ({ latitude, longitude, zoomLevel, poemsToDisplay, onZoomEnd, mapRef, onAboutOpen, onBugReportOpen, locationReady, hasCachedLocation, isVisible }) => {
  const recenterRef = useRef(null);
  const handleRecenter = useCallback(() => {
    if (recenterRef.current) recenterRef.current();
  }, []);

  const showSpinner = !hasCachedLocation && !locationReady;
  const initialZoom = hasCachedLocation && !locationReady ? CITY_ZOOM : zoomLevel;

  return (
    <div className="page-container">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', position: 'relative' }}>
        <IconButton
          onClick={onAboutOpen}
          aria-label="about"
          sx={{ position: 'absolute', left: 0 }}
        >
          <InfoIcon />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ my: 2 }}>GeoVerses</Typography>
        <IconButton
          onClick={onBugReportOpen}
          aria-label="report bug"
          sx={{ position: 'absolute', right: 0 }}
        >
          <BugReportIcon />
        </IconButton>
      </div>
      <div style={{ position: 'relative', width: '100%' }}>
        {showSpinner ? (
          <div className="map-spinner-container">
            <CircularProgress size={48} />
            <Typography variant="body2" sx={{ mt: 2, color: 'white' }}>Finding your location...</Typography>
          </div>
        ) : (
          <MapContainer
            center={[latitude, longitude]}
            ref={mapRef}
            zoom={initialZoom}
            scrollWheelZoom={false}
          >
            <RecenterControl
              latitude={latitude}
              longitude={longitude}
              recenterRef={recenterRef}
              locationReady={locationReady}
              zoomLevel={zoomLevel}
              isVisible={isVisible}
            />
            <ZoomTracker onZoomEnd={onZoomEnd} />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              edgeBufferTiles="2"
            />
            <Circle
              center={[latitude, longitude]}
              radius={20}
            />
            {poemsToDisplay.map((poem, index) =>
              <Marker key={poem.PoemTitle + index} position={[poem.latitude, poem.longitude]} icon={icon}>
                <Popup>
                  <span>{poem.PoemTitle}</span>
                </Popup>
              </Marker>
            )}
          </MapContainer>
        )}
        {!showSpinner && (
          <div className="recenter-button-container">
            <IconButton
              onClick={handleRecenter}
              aria-label="return to current location"
              sx={{ backgroundColor: 'white', '&:hover': { backgroundColor: '#e0e0e0' } }}
            >
              <MyLocationIcon sx={{ color: '#1976d2' }} />
            </IconButton>
          </div>
        )}
      </div>
    </div>
  );
};

export default PoemMap;

